import { Switch, Route, useLocation } from "wouter";
import { Suspense, useEffect } from "react";
import { Home } from "@/pages/home";
import { lazyRoute } from "@/lib/lazy-route";
import { recordNavigation } from "@/lib/demo-source";

// Home is eager — it's the LCP route and we want zero overhead on first paint.
// All other routes are lazy-loaded chunks; preloaded at SSR/prerender time and
// loaded on demand at runtime.
const ActiveListeningPage = lazyRoute(() => import("@/pages/active-listening").then((m) => ({ default: m.ActiveListeningPage })));
const RapidDeployPage = lazyRoute(() => import("@/pages/rapid-deploy").then((m) => ({ default: m.RapidDeployPage })));
const RestrictedListPage = lazyRoute(() => import("@/pages/restricted-list").then((m) => ({ default: m.RestrictedListPage })));
const WhyHarmCheckPage = lazyRoute(() => import("@/pages/why-harmcheck").then((m) => ({ default: m.WhyHarmCheckPage })));
const HarmCheckVsLLMsPage = lazyRoute(() => import("@/pages/harmcheck-vs-llms").then((m) => ({ default: m.HarmCheckVsLLMsPage })));
const PrivacyPolicyPage = lazyRoute(() => import("@/pages/privacy-policy").then((m) => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazyRoute(() => import("@/pages/terms-of-service").then((m) => ({ default: m.TermsOfServicePage })));
const TrustPrivacyPage = lazyRoute(() => import("@/pages/trust-privacy").then((m) => ({ default: m.TrustPrivacyPage })));
const TrustSecurityPage = lazyRoute(() => import("@/pages/trust-security").then((m) => ({ default: m.TrustSecurityPage })));
const TrustResponsibleAIPage = lazyRoute(() => import("@/pages/trust-responsible-ai").then((m) => ({ default: m.TrustResponsibleAIPage })));
const FAQsPage = lazyRoute(() => import("@/pages/faqs").then((m) => ({ default: m.FAQsPage })));
const BookADemoPage = lazyRoute(() => import("@/pages/book-a-demo").then((m) => ({ default: m.BookADemoPage })));
const HomePreviewPage = lazyRoute(() => import("@/pages/home-preview").then((m) => ({ default: m.HomePreview })));
const HomePreview2Page = lazyRoute(() => import("@/pages/home-preview2").then((m) => ({ default: m.HomePreview2 })));
const CapabilitiesPreviewPage = lazyRoute(() => import("@/pages/capabilities-preview").then((m) => ({ default: m.CapabilitiesPreview })));
const DiscoveryPreviewPage = lazyRoute(() => import("@/pages/solution-discovery-preview").then((m) => ({ default: m.DiscoveryPreviewPage })));
const ClassificationPreviewPage = lazyRoute(() => import("@/pages/solution-classification-preview").then((m) => ({ default: m.ClassificationPreviewPage })));
const PreventionPreviewPage = lazyRoute(() => import("@/pages/solution-prevention-preview").then((m) => ({ default: m.PreventionPreviewPage })));
const FinancialServicesPreviewPage = lazyRoute(() => import("@/pages/industry-financial-services-preview").then((m) => ({ default: m.FinancialServicesPreviewPage })));
const LawPreviewPage = lazyRoute(() => import("@/pages/industry-law-preview").then((m) => ({ default: m.LawPreviewPage })));
const HumanResourcesPreviewPage = lazyRoute(() => import("@/pages/industry-human-resources-preview").then((m) => ({ default: m.HumanResourcesPreviewPage })));
const InsurancePreviewPage = lazyRoute(() => import("@/pages/industry-insurance-preview").then((m) => ({ default: m.InsurancePreviewPage })));
const LPAiCompliancePage = lazyRoute(() => import("@/pages/lp-ai-compliance").then((m) => ({ default: m.LPAiCompliancePage })));
const FeedbackPage = lazyRoute(() => import("@/pages/feedback").then((m) => ({ default: m.FeedbackPage })));
const SupportPage = lazyRoute(() => import("@/pages/support").then((m) => ({ default: m.SupportPage })));
const AdminFeedbackPage = lazyRoute(() => import("@/pages/admin-feedback").then((m) => ({ default: m.AdminFeedbackPage })));
const AboutPage = lazyRoute(() => import("@/pages/about").then((m) => ({ default: m.AboutPage })));
const CaseStudiesPage = lazyRoute(() => import("@/pages/case-studies").then((m) => ({ default: m.CaseStudiesPage })));
const CaseStudyDetailPage = lazyRoute(() => import("@/pages/case-study-detail").then((m) => ({ default: m.CaseStudyDetailPage })));
const BlogPage = lazyRoute(() => import("@/pages/blog").then((m) => ({ default: m.BlogPage })));
const PostDetailPage = lazyRoute(() => import("@/pages/post-detail").then((m) => ({ default: m.PostDetailPage })));
const AdminBlogPage = lazyRoute(() => import("@/pages/admin-blog").then((m) => ({ default: m.AdminBlogPage })));
const AdminDemoRequestsPage = lazyRoute(() => import("@/pages/admin-demo-requests").then((m) => ({ default: m.AdminDemoRequestsPage })));
const NotFoundPage = lazyRoute(() => import("@/pages/not-found").then((m) => ({ default: m.NotFoundPage })));

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function TrackDemoSource() {
  const [location] = useLocation();
  useEffect(() => {
    recordNavigation(location);
  }, [location]);
  return null;
}

/**
 * App is just the routes. The <Router> wrapper is provided by main.tsx (CSR)
 * or entry-server.tsx (SSR/prerender) so each entry can configure routing
 * appropriately (browser history vs. ssrPath).
 *
 * The optional `ssrUrl` prop is accepted but currently unused — kept for
 * future per-route data needs without breaking the entry-server signature.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function App(_props: { ssrUrl?: string } = {}) {
  return (
    <>
      <ScrollToTop />
      <TrackDemoSource />
      <Suspense fallback={null}>
        <Switch>
          <Route path="/active-listening" component={ActiveListeningPage} />
          <Route path="/rapid-deploy" component={RapidDeployPage} />
          <Route path="/restricted-list" component={RestrictedListPage} />
          <Route path="/why-harmcheck" component={WhyHarmCheckPage} />
          <Route path="/harmcheck-vs-llms" component={HarmCheckVsLLMsPage} />
          <Route path="/privacy-policy" component={PrivacyPolicyPage} />
          <Route path="/terms-of-service" component={TermsOfServicePage} />
          <Route path="/trust-privacy" component={TrustPrivacyPage} />
          <Route path="/trust-security" component={TrustSecurityPage} />
          <Route
            path="/trust-responsible-ai"
            component={TrustResponsibleAIPage}
          />
          <Route path="/faqs" component={FAQsPage} />
          <Route path="/book-a-demo" component={BookADemoPage} />
          <Route path="/home-preview" component={HomePreviewPage} />
          <Route path="/home-preview2" component={HomePreview2Page} />
          <Route path="/home-preview/capabilities" component={CapabilitiesPreviewPage} />
          <Route path="/home-preview/solutions/discovery" component={DiscoveryPreviewPage} />
          <Route path="/home-preview/solutions/classification" component={ClassificationPreviewPage} />
          <Route path="/home-preview/solutions/prevention" component={PreventionPreviewPage} />
          <Route path="/home-preview/industry/financial-services" component={FinancialServicesPreviewPage} />
          <Route path="/home-preview/industry/law" component={LawPreviewPage} />
          <Route path="/home-preview/industry/human-resources" component={HumanResourcesPreviewPage} />
          <Route path="/home-preview/industry/insurance" component={InsurancePreviewPage} />
          <Route path="/lp/ai-compliance" component={LPAiCompliancePage} />
          <Route path="/feedback/outlook" component={FeedbackPage} />
          <Route path="/support" component={SupportPage} />
          <Route path="/admin/feedback" component={AdminFeedbackPage} />
          <Route path="/admin/blog" component={AdminBlogPage} />
          <Route path="/admin/demo-requests" component={AdminDemoRequestsPage} />
          <Route path="/about" component={AboutPage} />
          <Route path="/case-studies" component={CaseStudiesPage} />
          <Route path="/case-studies/:slug" component={CaseStudyDetailPage} />
          <Route path="/blog" component={BlogPage} />
          <Route path="/post/:slug" component={PostDetailPage} />
          <Route path="/" component={Home} />
          <Route component={NotFoundPage} />
        </Switch>
      </Suspense>
    </>
  );
}
